import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Copy, WhatsappLogo, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import AdminLayout from "@/components/AdminLayout";
import { OpBadge, PayBadge } from "@/components/StatusBadge";
import DeliveryTimeline from "@/components/DeliveryTimeline";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { apiErr } from "@/lib/api";
import { naira, fmtDateTime, CATEGORY_LABEL } from "@/lib/format";

const STATUSES = ["Quote Requested", "Quote Sent", "Quote Accepted", "Awaiting Payment",
  "Payment Confirmed", "Scheduled", "Preparing for Dispatch", "Dispatched", "In Transit", "Delivered", "Completed", "Cancelled"];

export default function AdminOrderDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/admin/orders/${id}`); setOrder(data); }
    catch (e) { toast.error(apiErr(e)); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!order) return <AdminLayout><div className="text-slate-500 py-20 text-center">Loading…</div></AdminLayout>;
  const f = order.financials;
  const shareLink = `${window.location.origin}/q/${order.share_token}`;

  return (
    <AdminLayout>
      <Link to="/admin/orders" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#0A4F35] mb-5">
        <ArrowLeft size={16} /> Back to orders
      </Link>

      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-display text-3xl font-bold text-slate-900">{order.order_number}</h1>
            <OpBadge status={order.operational_status} />
            <PayBadge status={f.payment_status} />
          </div>
          <p className="mt-1 text-slate-500">
            {order.company_name} · {order.customer_id
              ? <Link to={`/admin/customers/${order.customer_id}`} className="text-[#0A4F35] hover:underline">View customer</Link>
              : "Guest"} · {CATEGORY_LABEL[order.payment_category]}
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" data-testid="admin-copy-link"
            onClick={() => { navigator.clipboard.writeText(shareLink); toast.success("Secure link copied"); }}>
            <Copy size={16} className="mr-1" /> Copy Link
          </Button>
          <a href={`https://wa.me/?text=${encodeURIComponent("Your RODAIR quotation is ready. View and confirm here: " + shareLink)}`} target="_blank" rel="noreferrer">
            <Button size="sm" className="bg-[#16A34A] hover:bg-[#128a3d] text-white"><WhatsappLogo size={16} weight="fill" className="mr-1" /> WhatsApp</Button>
          </a>
        </div>
      </div>

      <div className="mt-6 grid lg:grid-cols-3 gap-6">
        {/* Left: details + financials + timeline */}
        <div className="lg:col-span-2 space-y-6">
          <Card title="Order Details">
            <div className="grid sm:grid-cols-2 gap-y-3 gap-x-6 text-sm">
              <Field label="Contact" value={order.contact_name || "—"} />
              <Field label="Phone" value={order.phone} />
              <Field label="Email" value={order.email} />
              <Field label="Fuel" value={order.fuel_type} className="capitalize" />
              <Field label="Litres" value={Number(order.litres).toLocaleString()} />
              <Field label="Requested date" value={order.requested_date || "—"} />
              <Field label="Delivery address" value={order.delivery_address || "—"} />
              <Field label="PO / Ref" value={order.po_reference || "—"} />
              {order.special_instructions ? <div className="sm:col-span-2"><Field label="Special instructions" value={order.special_instructions} /></div> : null}
            </div>
          </Card>

          <Card title="Financial Summary">
            <div className="space-y-2 text-sm">
              <Row l={`Fuel (${Number(order.litres).toLocaleString()} L × ${naira(order.price_per_litre)})`} v={naira(f.subtotal)} />
              {order.delivery_charge > 0 && <Row l="Delivery charge" v={naira(order.delivery_charge)} />}
              {order.tax_amount > 0 && <Row l="Tax" v={naira(order.tax_amount)} />}
              <div className="border-t border-[#E5E7EB]" />
              <Row l="Total" v={naira(f.total)} bold />
              <Row l="Required before dispatch" v={naira(f.required_before_dispatch)} />
              <Row l="Paid to date" v={naira(f.amount_paid)} />
              <Row l="Outstanding" v={naira(f.outstanding)} bold danger={f.outstanding > 0} />
            </div>
            <div className={`mt-4 rounded-md px-4 py-3 text-sm font-medium ${f.dispatch_allowed ? "bg-emerald-50 text-emerald-800" : "bg-[#FBF3D5] text-[#7A5C00]"}`} data-testid="dispatch-status">
              {f.dispatch_allowed ? "✓ Payment threshold met — dispatch permitted." : `Dispatch locked — ${naira(f.required_before_dispatch - f.amount_paid)} required before dispatch.`}
              {order.override_dispatch && <span className="block mt-1 text-xs">Admin override active: {order.override_reason}</span>}
            </div>
          </Card>

          <Card title="Payments">
            {order.payments?.length ? (
              <div className="divide-y divide-[#E5E7EB] -my-2">
                {order.payments.map((p) => (
                  <div key={p.id} className="py-3 flex items-center justify-between text-sm">
                    <div>
                      <p className="font-medium text-slate-900">{naira(p.amount)} <span className="text-slate-400 font-normal">· {p.method}</span></p>
                      <p className="text-xs text-slate-400">{fmtDateTime(p.paid_at)} · {p.reference} · by {p.recorded_by}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-slate-400">No payments recorded yet.</p>}
          </Card>

          {order.operational_status !== "Quote Requested" && (
            <Card title="Delivery Progress"><DeliveryTimeline steps={order.delivery_progress} /></Card>
          )}
        </div>

        {/* Right: actions */}
        <div className="space-y-6">
          <QuoteBuilder order={order} onDone={load} busy={busy} setBusy={setBusy} />
          <StatusUpdater order={order} onDone={load} busy={busy} setBusy={setBusy} dispatchAllowed={f.dispatch_allowed} />
          <PaymentRecorder order={order} onDone={load} busy={busy} setBusy={setBusy} />
        </div>
      </div>
    </AdminLayout>
  );
}

function QuoteBuilder({ order, onDone, busy, setBusy }) {
  const [form, setForm] = useState({
    price_per_litre: order.price_per_litre || 0,
    delivery_charge: order.delivery_charge || 0,
    tax_amount: order.tax_amount || 0,
    payment_category: order.payment_category || "new_standard",
    deposit_percent: order.deposit_percent || 20,
    payment_due_date: order.payment_due_date ? order.payment_due_date.slice(0, 10) : "",
    quote_notes: order.quote_notes || "",
  });
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { ...form, price_per_litre: Number(form.price_per_litre), delivery_charge: Number(form.delivery_charge),
        tax_amount: Number(form.tax_amount), deposit_percent: Number(form.deposit_percent),
        payment_due_date: form.payment_due_date ? new Date(form.payment_due_date).toISOString() : null };
      await api.put(`/admin/orders/${order.id}/quote`, payload);
      toast.success("Quotation sent to customer");
      onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Card title="Build & Send Quote">
      <div className="space-y-3">
        <div><Label>Price per litre (₦)</Label><Input data-testid="q-ppl" type="number" value={form.price_per_litre} onChange={set("price_per_litre")} className="mt-1" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Delivery charge</Label><Input data-testid="q-delivery" type="number" value={form.delivery_charge} onChange={set("delivery_charge")} className="mt-1" /></div>
          <div><Label>Tax</Label><Input data-testid="q-tax" type="number" value={form.tax_amount} onChange={set("tax_amount")} className="mt-1" /></div>
        </div>
        <div>
          <Label>Payment category</Label>
          <Select value={form.payment_category} onValueChange={(v) => setForm((s) => ({ ...s, payment_category: v }))}>
            <SelectTrigger data-testid="q-category" className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="new_standard">New / Standard (100% prepaid)</SelectItem>
              <SelectItem value="deposit">Deposit customer</SelectItem>
              <SelectItem value="credit">Credit / contract</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {form.payment_category === "deposit" && (
          <div><Label>Deposit %</Label><Input data-testid="q-deposit" type="number" value={form.deposit_percent} onChange={set("deposit_percent")} className="mt-1" /></div>
        )}
        {form.payment_category === "credit" && (
          <div><Label>Payment due date</Label><Input data-testid="q-due" type="date" value={form.payment_due_date} onChange={set("payment_due_date")} className="mt-1" /></div>
        )}
        <div><Label>Quote notes</Label><Textarea data-testid="q-notes" value={form.quote_notes} onChange={set("quote_notes")} className="mt-1" placeholder="Optional message on the quotation" /></div>
        <Button data-testid="send-quote-btn" onClick={submit} disabled={busy} className="w-full bg-[#0A4F35] hover:bg-[#083D29] text-white font-semibold">
          {order.operational_status === "Quote Requested" ? "Send Quotation" : "Update & Re-send Quote"}
        </Button>
      </div>
    </Card>
  );
}

function StatusUpdater({ order, onDone, busy, setBusy, dispatchAllowed }) {
  const [status, setStatus] = useState(order.operational_status);
  const [override, setOverride] = useState(false);
  const [reason, setReason] = useState("");

  const submit = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/orders/${order.id}/status`, { operational_status: status, override, override_reason: reason });
      toast.success("Status updated");
      setOverride(false); setReason("");
      onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Card title="Operational Status">
      <Select value={status} onValueChange={setStatus}>
        <SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
        <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
      </Select>
      {!dispatchAllowed && (
        <div className="mt-3 rounded-md bg-[#FBF3D5] p-3">
          <div className="flex items-start gap-2 text-xs text-[#7A5C00]">
            <WarningCircle size={16} className="mt-0.5 shrink-0" />
            <span>Dispatch is locked until the required payment is met. Override requires an administrator and is recorded in the audit log.</span>
          </div>
          <label className="mt-2 flex items-center gap-2 text-sm text-slate-700">
            <Checkbox data-testid="override-check" checked={override} onCheckedChange={setOverride} /> Override payment requirement
          </label>
          {override && <Input data-testid="override-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for override" className="mt-2" />}
        </div>
      )}
      <Button data-testid="update-status-btn" onClick={submit} disabled={busy} className="mt-3 w-full bg-[#E5B800] hover:bg-[#CCA300] text-black font-semibold">
        Update Status
      </Button>
    </Card>
  );
}

function PaymentRecorder({ order, onDone, busy, setBusy }) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("bank_transfer");
  const [reference, setReference] = useState("");

  const submit = async () => {
    if (!amount || Number(amount) <= 0) { toast.error("Enter a valid amount"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/orders/${order.id}/payments`, { amount: Number(amount), method, reference });
      toast.success("Payment recorded");
      setAmount(""); setReference("");
      onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Card title="Record Payment (Offline)">
      <div className="space-y-3">
        <div><Label>Amount (₦)</Label><Input data-testid="rec-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1" /></div>
        <div>
          <Label>Method</Label>
          <Select value={method} onValueChange={setMethod}>
            <SelectTrigger data-testid="rec-method" className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="bank_transfer">Bank transfer</SelectItem>
              <SelectItem value="cash">Cash</SelectItem>
              <SelectItem value="cheque">Cheque</SelectItem>
              <SelectItem value="pos">POS</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label>Reference</Label><Input data-testid="rec-ref" value={reference} onChange={(e) => setReference(e.target.value)} className="mt-1" placeholder="Transaction / teller no." /></div>
        <Button data-testid="record-payment-btn" onClick={submit} disabled={busy} className="w-full bg-[#0A4F35] hover:bg-[#083D29] text-white font-semibold">Record Payment</Button>
      </div>
    </Card>
  );
}

function Card({ title, children }) {
  return (
    <div className="rounded-lg border border-[#E5E7EB] bg-white">
      <div className="px-5 py-3 border-b border-[#E5E7EB]"><h3 className="font-display font-semibold text-slate-900">{title}</h3></div>
      <div className="p-5">{children}</div>
    </div>
  );
}
function Field({ label, value, className = "" }) {
  return (<div><div className="text-xs uppercase tracking-widest text-slate-400">{label}</div><div className={`text-slate-800 font-medium ${className}`}>{value}</div></div>);
}
function Row({ l, v, bold, danger }) {
  return (<div className="flex items-center justify-between"><span className={bold ? "font-semibold text-slate-900" : "text-slate-600"}>{l}</span><span className={`tabular-nums ${bold ? "font-bold text-slate-900" : ""} ${danger ? "text-[#DC2626]" : ""}`}>{v}</span></div>);
}
